import React, { useEffect, useState } from 'react';
import { FieldArray, getIn, useFormikContext } from 'formik';
import i18n from 'i18n-js';
import Button from '@material-ui/core/Button';
import {
    Box,
    ButtonBase,
    Checkbox,
    Collapse,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import { FormikTimePicker, Input, StyledChip } from 'components/shared';
import { MenuType } from 'constants/enums';
import { useStyles } from '../../../styles';
import { useAppSelector } from 'index';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { getDaysSelectOptions } from '..';
import { getAllPricingLevels } from 'helpers/pricingLevelHelper';
import {
    formatPickupTime,
    formatPickupDate,
    getDiscountedPrice,
    getOrderingStartWithOffset,
    getTakeAwayPickupRange,
    getTodayWorkingHours,
    getNextWorkingDayLabel,
    hasOwnOrSizeError,
    getWorkingHoursRange,
} from 'helpers/orderInAdvanceHelper';
import { OrderInAdvanceStockSource } from 'store/menuDetailsStore/reducer';

export interface PriceManagementTabProps {
    isFieldDisabled: (name: string, additionalOption?: boolean) => boolean;
}

type OfferKind = 'orderInAdvanceSameDay' | 'orderInAdvanceCustom';

export default function PriceManagementTab({ isFieldDisabled }: PriceManagementTabProps) {
    const classes = useStyles();
    const { values, errors } = useFormikContext<any>();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const menuType = useAppSelector((state) => state.menuDetails.menu.type);
    const pricingLevels = getAllPricingLevels(restaurant, menuType === MenuType.Catalog);
    const isPriceLevelDisabled = isFieldDisabled('priceLevel') || Boolean(values.isCustomPrice);
    const isOrderInAdvanceDisabled = isFieldDisabled('orderInAdvance');
    const [priceLevelsExpanded, setPriceLevelsExpanded] = useExpandedSection(
        false,
        hasOwnOrSizeError(errors, 'priceLevels')
    );
    const [orderInAdvanceExpanded, setOrderInAdvanceExpanded] = useExpandedSection(
        false,
        hasOwnOrSizeError(errors, 'orderInAdvanceSameDay') ||
            hasOwnOrSizeError(errors, 'orderInAdvanceCustom')
    );
    const activeOfferLabels = [
        values.orderInAdvanceSameDay?.isEnabled && i18n.t('common.sameDayPickup'),
        values.orderInAdvanceCustom?.isEnabled && i18n.t('common.customSchedule'),
    ].filter(Boolean);

    return (
        <>
            {!!values.priceLevels?.length && (
                <>
                    <Box
                        component="h6"
                        className={classes.dishFormSectionTitle}
                        display="flex"
                        alignItems="center"
                    >
                        {i18n.t('common.setPriceLevels')}
                        <Box marginLeft="auto">
                            <ExpandButton
                                expanded={priceLevelsExpanded}
                                onClick={() => setPriceLevelsExpanded(!priceLevelsExpanded)}
                            />
                        </Box>
                    </Box>
                    <Collapse in={priceLevelsExpanded}>
                        <Table className={classes.sizesAndPricesTable}>
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    {values.priceLevels.map((level: any) => (
                                        <TableCell key={level.priceLevelId}>
                                            {pricingLevels.find(
                                                (pricingLevel) =>
                                                    pricingLevel.id === level.priceLevelId
                                            )?.name || 'price level'}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <PriceLevelRow
                                    label={values.size}
                                    path="priceLevels"
                                    levels={values.priceLevels}
                                    errors={errors}
                                    disabled={isPriceLevelDisabled}
                                />
                                {values.additionalSizes.map((size: any, index: number) => (
                                    <PriceLevelRow
                                        key={index}
                                        label={size.size}
                                        path={`additionalSizes[${index}].priceLevels`}
                                        levels={size.priceLevels}
                                        errors={errors}
                                        disabled={isPriceLevelDisabled}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </Collapse>
                </>
            )}
            {restaurant.isOrderInAdvance && restaurant.takeAway.isActive && (
                <>
                    <Box
                        component="h6"
                        className={classes.dishFormSectionTitle}
                        display="flex"
                        alignItems="center"
                    >
                        {i18n.t('restaurant.orderInAdvance')}
                        {activeOfferLabels.map((label) => (
                            <StyledChip
                                key={label}
                                size="small"
                                label={label.toUpperCase()}
                                labelFontSize={10}
                                labelColor="#99567e"
                                style={{
                                    height: 18,
                                    marginLeft: 10,
                                    backgroundColor: '#f3e6ee',
                                }}
                            />
                        ))}
                        <Box marginLeft="auto">
                            <ExpandButton
                                expanded={orderInAdvanceExpanded}
                                onClick={() => setOrderInAdvanceExpanded(!orderInAdvanceExpanded)}
                            />
                        </Box>
                    </Box>
                    <Collapse in={orderInAdvanceExpanded}>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginBottom: 8 }}
                        >
                            {i18n.t('common.orderInAdvanceIndependentHint')}
                        </Typography>
                        <OfferConfiguration
                            kind="orderInAdvanceSameDay"
                            title={i18n.t('common.sameDayPickup')}
                            disabled={isOrderInAdvanceDisabled}
                        />
                        <OfferConfiguration
                            kind="orderInAdvanceCustom"
                            title={i18n.t('common.customSchedule')}
                            disabled={isOrderInAdvanceDisabled}
                        />
                    </Collapse>
                </>
            )}
        </>
    );
}

function useExpandedSection(initiallyExpanded: boolean, hasError: boolean) {
    const [expanded, setExpanded] = useState(initiallyExpanded);

    useEffect(() => {
        if (hasError) {
            setExpanded(true);
        }
    }, [hasError]);

    return [expanded, setExpanded] as const;
}

function ExpandButton({ expanded, onClick }: { expanded: boolean; onClick: () => void }) {
    return (
        <Button
            color="primary"
            size="small"
            startIcon={
                expanded ? (
                    <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                    <KeyboardArrowDownIcon fontSize="small" />
                )
            }
            onClick={onClick}
        >
            {expanded ? i18n.t('common.hide').toUpperCase() : i18n.t('common.setUp')}
        </Button>
    );
}

function PriceLevelRow({ label, path, levels, errors, disabled }: any) {
    return (
        <TableRow>
            <TableCell>{label}</TableCell>
            {levels.map((level: any, index: number) => {
                const name = `${path}[${index}].price`;
                return (
                    <TableCell key={level.priceLevelId}>
                        <Input
                            disabled={disabled}
                            variant="filled"
                            name={name}
                            type="text"
                            label="CHF"
                            error={getIn(errors, name)}
                            showErrorBelow
                            style={{ marginBottom: 5 }}
                        />
                    </TableCell>
                );
            })}
        </TableRow>
    );
}

function OfferConfiguration({
    kind,
    title,
    disabled,
}: {
    kind: OfferKind;
    title: string;
    disabled: boolean;
}) {
    const { values, errors, setFieldValue } = useFormikContext<any>();
    const workingHours = useAppSelector(
        (state) => state.restaurantDetails.restaurantInformation.takeAway?.workingHours
    );
    const lang = useAppSelector((state) => state.global.language);
    const offer = values[kind];
    const isCustom = kind === 'orderInAdvanceCustom';
    const [expanded, setExpanded] = useExpandedSection(
        !!offer?.isEnabled,
        hasOwnOrSizeError(errors, kind)
    );
    const workingHoursRange = getTakeAwayPickupRange(workingHours);
    const todayWorkingHours = getTodayWorkingHours(workingHours);
    const isDedicated = offer?.stockSource === OrderInAdvanceStockSource.Dedicated;
    const rows = [
        { key: 'main', label: values.size || values.name, price: values.price, path: kind, offer },
        ...values.additionalSizes.map((size: any, index: number) => ({
            key: `size-${index}`,
            label: size.size,
            price: size.price,
            path: `additionalSizes[${index}].${kind}`,
            offer: size[kind],
        })),
    ];

    function updateAll(field: string, value: any) {
        rows.forEach((row) => setFieldValue(`${row.path}.${field}`, value));
    }

    const orderingStart = getOrderingStartWithOffset(
        offer?.orderingFollowsWorkingHours
            ? { from: todayWorkingHours[0]?.from ?? null, to: todayWorkingHours[0]?.to ?? null }
            : offer?.orderingWindow ?? workingHoursRange,
        offer?.orderingOffsetHours
    );

    return (
        <Box marginTop={isCustom ? 2 : 0} paddingTop={isCustom ? 2 : 0}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" marginLeft="-11px">
                    <Switch
                        color="primary"
                        checked={!!offer?.isEnabled}
                        disabled={disabled}
                        inputProps={{ 'aria-label': title }}
                        onChange={() => {
                            const enabled = !offer?.isEnabled;
                            updateAll('isEnabled', enabled);
                            setExpanded(enabled);
                        }}
                    />
                    <ButtonBase style={{ gap: 12 }} onClick={() => setExpanded(!expanded)}>
                        <Typography variant="body1" style={{ whiteSpace: 'nowrap' }}>
                            {title}
                        </Typography>
                        {!isCustom && (
                            <Typography variant="body2" color="textSecondary">
                                {i18n.t('common.sameDayPickupSummary')}
                            </Typography>
                        )}
                    </ButtonBase>
                </Box>
                {isCustom && (
                    <ExpandButton expanded={expanded} onClick={() => setExpanded(!expanded)} />
                )}
            </Box>
            <ValidationMessages
                messages={typeof getIn(errors, kind) === 'string' ? [getIn(errors, kind)] : []}
            />
            <Collapse in={expanded}>
                {(kind === 'orderInAdvanceSameDay' || isCustom) && (
                    <FormControlLabel
                        control={
                            <Switch
                                color="primary"
                                checked={isDedicated}
                                disabled={disabled || !offer?.isEnabled}
                                onChange={(_, checked) =>
                                    updateAll(
                                        'stockSource',
                                        checked
                                            ? OrderInAdvanceStockSource.Dedicated
                                            : OrderInAdvanceStockSource.Main
                                    )
                                }
                            />
                        }
                        label={
                            <Box display="flex" flexDirection="column" gridGap={3}>
                                <Box display="flex" alignItems="center" gridGap={8}>
                                    <Typography variant="body1">
                                        {i18n.t('common.trackPreOrderQuantity')}
                                    </Typography>
                                    {isDedicated ? (
                                        <StyledChip
                                            size="small"
                                            label={i18n.t('common.trackedSeparately')}
                                            labelFontSize={10}
                                            labelColor="#99567e"
                                            style={{
                                                height: 18,
                                                backgroundColor: '#f3e6ee',
                                            }}
                                        />
                                    ) : (
                                        <StyledChip
                                            size="small"
                                            label={i18n.t('common.fromMainStock')}
                                            labelFontSize={10}
                                            labelColor="#7a7078"
                                            style={{
                                                height: 18,
                                                backgroundColor: '#efedee',
                                            }}
                                        />
                                    )}
                                </Box>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    style={{ fontSize: 13 }}
                                >
                                    {i18n.t(
                                        isDedicated
                                            ? 'common.preOrderQuantityTrackedHint'
                                            : 'common.preOrderQuantityMainStockHint'
                                    )}
                                </Typography>
                            </Box>
                        }
                    />
                )}
                {isCustom ? (
                    <CustomWindows
                        path={`${kind}.daySettings`}
                        values={values}
                        disabled={disabled || !offer?.isEnabled}
                        days={getDaysSelectOptions(lang)}
                        follows={!!offer?.orderingFollowsWorkingHours}
                        onChange={(value: boolean) =>
                            updateAll('orderingFollowsWorkingHours', value)
                        }
                        todayWorkingHours={todayWorkingHours}
                    />
                ) : (
                    <OfferHours
                        label={i18n.t('common.orderingAvailability')}
                        path={`${kind}.orderingWindow`}
                        follows={!!offer?.orderingFollowsWorkingHours}
                        workingHoursRange={workingHoursRange}
                        todayWorkingHours={todayWorkingHours}
                        disabled={disabled || !offer?.isEnabled}
                        onChange={(value: boolean) =>
                            updateAll('orderingFollowsWorkingHours', value)
                        }
                    />
                )}
                {kind === 'orderInAdvanceSameDay' && (
                    <Box display="flex" alignItems="center" marginTop={2}>
                        <Typography
                            variant="body2"
                            style={{
                                width: 185,
                                minWidth: 185,
                                whiteSpace: 'nowrap',
                                fontSize: 13,
                                alignSelf: 'stretch',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {i18n.t('common.openForOrderingEarlierBy')}
                        </Typography>
                        <Box width={120} marginRight={0}>
                            <Input
                                fullWidth
                                disabled={disabled || !offer?.isEnabled}
                                name={`${kind}.orderingOffsetHours`}
                                type="text"
                                variant="filled"
                                label={i18n.t('common.offset')}
                                error={getIn(errors, `${kind}.orderingOffsetHours`)}
                                showErrorBelow
                                inputProps={{ min: 0, max: 12, step: 1 }}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const value = event.target.value.replace(/\D/g, '').slice(0, 2);

                                    setFieldValue(`${kind}.orderingOffsetHours`, value);
                                }}
                                style={{ marginBottom: 0 }}
                            />
                        </Box>
                        {orderingStart != null && (
                            <Typography
                                variant="body2"
                                color="primary"
                                style={{ whiteSpace: 'nowrap', fontWeight: 500, marginLeft: 12 }}
                            >
                                {`→  ${i18n.t('common.orderingFrom', {
                                    time: formatPickupTime(orderingStart),
                                })}`}
                            </Typography>
                        )}
                    </Box>
                )}
                {isCustom ? (
                    <CustomPickupHours
                        offer={offer}
                        disabled={disabled || !offer?.isEnabled}
                        workingHours={workingHours}
                        workingHoursRange={workingHoursRange}
                        todayWorkingHours={todayWorkingHours}
                    />
                ) : (
                    <OfferHours
                        label={i18n.t('common.pickupHours')}
                        path={`${kind}.pickupWindow`}
                        follows={!!offer?.pickupFollowsWorkingHours}
                        workingHoursRange={workingHoursRange}
                        todayWorkingHours={todayWorkingHours}
                        disabled={disabled || !offer?.isEnabled}
                        onChange={(value: boolean) => updateAll('pickupFollowsWorkingHours', value)}
                    />
                )}
                <OfferSizeTable
                    rows={rows}
                    errors={errors}
                    isDedicated={isDedicated}
                    disabled={disabled}
                />
            </Collapse>
        </Box>
    );
}

function CustomPickupHours({ offer, disabled, workingHours, todayWorkingHours }: any) {
    const { errors, setFieldValue } = useFormikContext<any>();
    const follows = !!offer?.pickupFollowsWorkingHours;
    const workingHoursRange = getTakeAwayPickupRange(workingHours);
    const pickupWorkingHoursRange = todayWorkingHours.length
        ? getWorkingHoursRange(todayWorkingHours)
        : workingHoursRange;
    const pickupDate =
        formatPickupDate(offer?.nextWorkingDayOption?.date) ||
        getNextWorkingDayLabel(workingHours) ||
        '—';

    return (
        <Box marginTop={2}>
            <FormControlLabel
                control={
                    <Checkbox
                        color="primary"
                        checked={follows}
                        disabled={disabled}
                        onChange={(_, checked) =>
                            setFieldValue('orderInAdvanceCustom.pickupFollowsWorkingHours', checked)
                        }
                    />
                }
                label={
                    <Box display="flex" flexDirection="column" gridGap={3}>
                        <Typography variant="body1">
                            {i18n.t('common.pickupFollowsWorkingHours')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ fontSize: 13 }}>
                            {!follows
                                ? i18n.t('common.pickupWindowCustomHint')
                                : follows && !todayWorkingHours.length
                                ? i18n.t('common.restaurantClosedToday')
                                : i18n.t('common.pickupWorkingHoursAutoUpdate', {
                                      hours: todayWorkingHours
                                          .map((range: any) =>
                                              `${formatPickupTime(range.from)} - ${formatPickupTime(
                                                  range.to
                                              )}`.slice(0, 2)
                                          )
                                          .join(', '),
                                  })}
                        </Typography>
                    </Box>
                }
            />
            {!follows && (
                <Box display="flex" alignItems="center" marginTop={1}>
                    <Typography
                        variant="body2"
                        style={{
                            width: 110,
                            minWidth: 110,
                            fontSize: 13,
                        }}
                    >
                        {i18n.t('common.pickupHoursLabel')}
                    </Typography>
                    <FormikTimePicker
                        name="orderInAdvanceCustom.pickupWindow.from"
                        size="small"
                        label={i18n.t('form.availableFrom')}
                        disabled={disabled || follows}
                        clampToRange={pickupWorkingHoursRange}
                        showErrorBelow
                        style={{ marginBottom: 0 }}
                    />
                    <Box
                        width={18}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        color="text.secondary"
                    >
                        -
                    </Box>
                    <FormikTimePicker
                        name="orderInAdvanceCustom.pickupWindow.to"
                        size="small"
                        label={i18n.t('form.availableTo')}
                        disabled={disabled || follows}
                        clampToRange={pickupWorkingHoursRange}
                        showErrorBelow
                        style={{ marginBottom: 0 }}
                    />
                    {/* <Typography variant="body2" color="textSecondary" style={{ marginLeft: 10 }}>
                        {follows ? i18n.t('common.inheritedReadOnly') : i18n.t('common.editable')}
                    </Typography> */}
                </Box>
            )}
            {!follows && <WorkingHoursHint workingHours={todayWorkingHours} />}
            <ValidationMessages
                messages={getValidationMessages(getIn(errors, 'orderInAdvanceCustom.pickupWindow'))}
            />
            <Box display="flex" alignItems="center" marginTop={1}>
                <Typography variant="body2" style={{ width: 78, minWidth: 78, fontSize: 13 }}>
                    {i18n.t('common.pickupDay')}
                </Typography>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    bgcolor="action.hover"
                    padding="6px 12px"
                    width={180}
                    height={48}
                >
                    <Typography variant="caption" color="textSecondary">
                        {i18n.t('common.rule')}
                    </Typography>
                    <Typography variant="body2">{i18n.t('common.firstNextWorkingDay')}</Typography>
                </Box>
                <Typography variant="body2" color="primary" style={{ marginLeft: 10 }}>
                    {`→  ${pickupDate}`}
                </Typography>
            </Box>
            {/* {follows && (
                <Typography variant="body2" color="textSecondary">
                    {todayWorkingHours.length
                        ? i18n.t('common.workingHoursAutoUpdate', {
                              hours: todayWorkingHours
                                  .map(
                                      (range: any) =>
                                          `${formatPickupTime(range.from)} - ${formatPickupTime(
                                              range.to
                                          )}`
                                  )
                                  .join(', '),
                          })
                        : i18n.t('common.restaurantClosedToday')}
                </Typography>
            )} */}
        </Box>
    );
}

function OfferHours({
    label,
    path,
    follows,
    workingHoursRange,
    todayWorkingHours,
    disabled,
    onChange,
}: any) {
    const { errors } = useFormikContext<any>();
    const isOrderingAvailability = label === i18n.t('common.orderingAvailability');
    const isPickupHours = label === i18n.t('common.pickupHours');
    const todayWorkingHoursRange = todayWorkingHours.length
        ? getWorkingHoursRange(todayWorkingHours)
        : workingHoursRange;
    const inheritsWorkingHours = isOrderingAvailability || isPickupHours;
    const timeRangeLabel = isOrderingAvailability
        ? i18n.t('common.orderingHours')
        : isPickupHours
        ? i18n.t('common.pickupHoursLabel')
        : label;
    return (
        <Box marginTop={2}>
            <FormControlLabel
                control={
                    <Checkbox
                        color="primary"
                        checked={follows}
                        disabled={disabled}
                        onChange={(_, checked) => onChange(checked)}
                    />
                }
                label={
                    <Box display="flex" flexDirection="column" gridGap={3}>
                        <Typography variant="body1">
                            {label === i18n.t('common.pickupHours')
                                ? i18n.t('common.pickupFollowsWorkingHours')
                                : i18n.t('common.orderingFollowsWorkingHours')}
                        </Typography>
                        {inheritsWorkingHours && follows && (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ fontSize: 13 }}
                            >
                                {inheritsWorkingHours && !todayWorkingHours.length
                                    ? i18n.t('common.restaurantClosedToday')
                                    : i18n.t(
                                          isOrderingAvailability
                                              ? 'common.orderingWorkingHoursAutoUpdate'
                                              : 'common.pickupWorkingHoursAutoUpdate',
                                          {
                                              hours: todayWorkingHours
                                                  .map(
                                                      (range: any) =>
                                                          `${formatPickupTime(
                                                              range.from
                                                          )} - ${formatPickupTime(range.to)}`
                                                  )
                                                  .join(', '),
                                          }
                                      )}
                            </Typography>
                        )}
                    </Box>
                }
            />
            {!follows && (
                <Box display="flex" alignItems="center">
                    <Typography
                        variant="body2"
                        style={{
                            width: 110,
                            minWidth: 110,
                            fontSize: 13,
                            alignSelf: 'stretch',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {timeRangeLabel}
                    </Typography>
                    <FormikTimePicker
                        name={`${path}.from`}
                        size="small"
                        label={i18n.t('form.availableFrom')}
                        disabled={disabled || follows}
                        clampToRange={
                            isOrderingAvailability || isPickupHours
                                ? todayWorkingHoursRange
                                : undefined
                        }
                        showErrorBelow
                        style={{ marginBottom: 0, marginRight: 5 }}
                    />
                    {' - '}
                    <FormikTimePicker
                        name={`${path}.to`}
                        size="small"
                        label={i18n.t('form.availableTo')}
                        disabled={disabled || follows}
                        clampToRange={
                            isOrderingAvailability || isPickupHours
                                ? todayWorkingHoursRange
                                : undefined
                        }
                        showErrorBelow
                        style={{ marginBottom: 0, marginLeft: 5 }}
                    />
                    {inheritsWorkingHours && follows && (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginLeft: 12 }}
                        >
                            {i18n.t('common.inheritedReadOnly')}
                        </Typography>
                    )}
                </Box>
            )}
            {isPickupHours && !follows && <WorkingHoursHint workingHours={todayWorkingHours} />}
            {isOrderingAvailability && !follows && (
                <Typography variant="body2" color="textSecondary" style={{ fontSize: 13 }}>
                    {todayWorkingHours.length
                        ? i18n.t('common.orderingWindowMustStayWithinWorkingHoursHint', {
                              hours: todayWorkingHours
                                  .map(
                                      (range: any) =>
                                          `${formatPickupTime(range.from)} - ${formatPickupTime(
                                              range.to
                                          )}`
                                  )
                                  .join(', '),
                          })
                        : i18n.t('common.restaurantClosedToday')}
                </Typography>
            )}
            <ValidationMessages messages={getValidationMessages(getIn(errors, path))} />
        </Box>
    );
}

function WorkingHoursHint({ workingHours }: { workingHours: { from: number; to: number }[] }) {
    return (
        <Typography variant="body2" color="textSecondary" style={{ fontSize: 13 }}>
            {workingHours.length
                ? i18n.t('common.orderingWindowMustStayWithinWorkingHoursHint', {
                      hours: workingHours
                          .map(
                              (range) =>
                                  `${formatPickupTime(range.from)} - ${formatPickupTime(range.to)}`
                          )
                          .join(', '),
                  })
                : i18n.t('common.restaurantClosedToday')}
        </Typography>
    );
}

function OfferSizeTable({ rows, errors, isDedicated, disabled }: any) {
    const { setFieldValue } = useFormikContext<any>();
    const classes = useStyles();
    return (
        <Table className={classes.orderInAdvanceTable}>
            {/* <colgroup>
                <col style={{ width: isDedicated ? '18%' : '20%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: isDedicated ? '39%' : '58%' }} />
                {isDedicated && <col style={{ width: '21%' }} />}
            </colgroup> */}
            <TableHead>
                <TableRow>
                    <TableCell>{i18n.t('common.preOrderSize')}</TableCell>
                    <TableCell>{i18n.t('common.discountPercent')}</TableCell>
                    <TableCell>{i18n.t('common.priceCHF')}</TableCell>
                    {isDedicated && <TableCell>{i18n.t('common.preOrderQuantity')}</TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row: any) => {
                    const isBaseSize = row.key === 'main';
                    const enabled = isBaseSize
                        ? row.offer?.isBaseSizeEnabled !== false
                        : !!row.offer?.isEnabled;
                    const discountName = `${row.path}.discountPercent`;
                    const rowErrors = getValidationMessages(
                        getIn(errors, `${row.path}.orderingWindow`)
                    );
                    return (
                        <React.Fragment key={row.key}>
                            <TableRow>
                                <TableCell style={{ verticalAlign: enabled ? 'top' : 'middle' }}>
                                    <Box display="flex" alignItems="flex-start">
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    color="primary"
                                                    checked={enabled}
                                                    disabled={disabled}
                                                    onChange={() =>
                                                        setFieldValue(
                                                            `${row.path}.${
                                                                isBaseSize
                                                                    ? 'isBaseSizeEnabled'
                                                                    : 'isEnabled'
                                                            }`,
                                                            !enabled
                                                        )
                                                    }
                                                />
                                            }
                                            label={
                                                <Box display="flex" flexDirection="column">
                                                    <Typography
                                                        variant="body1"
                                                        /* style={{ whiteSpace: 'nowrap' }} */
                                                    >
                                                        {row.label}
                                                    </Typography>
                                                    {!enabled && (
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                            style={{
                                                                whiteSpace: 'nowrap',
                                                                fontSize: 12,
                                                                paddingRight: 16,
                                                            }}
                                                        >
                                                            {i18n.t('common.sizeNotOfferedSameDay')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell style={{ verticalAlign: enabled ? 'top' : 'middle' }}>
                                    {enabled ? (
                                        <Input
                                            disabled={disabled}
                                            name={discountName}
                                            type="text"
                                            size="small"
                                            variant="filled"
                                            label={i18n.t('common.discountPercent')}
                                            error={getIn(errors, discountName)}
                                            showErrorBelow
                                            style={{ maxWidth: 120 }}
                                        />
                                    ) : (
                                        <Typography variant="body1">-</Typography>
                                    )}
                                </TableCell>
                                <TableCell style={{ verticalAlign: enabled ? 'top' : 'middle' }}>
                                    {enabled ? (
                                        <>
                                            <Typography>
                                                {getDiscountedPrice(
                                                    row.price,
                                                    row.offer?.discountPercent
                                                ).toFixed(2)}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {i18n.t('common.standardPrice', {
                                                    price: Number(row.price || 0).toFixed(2),
                                                })}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography variant="body1">-</Typography>
                                    )}
                                </TableCell>
                                {isDedicated && (
                                    <TableCell
                                        style={{ verticalAlign: enabled ? 'top' : 'middle' }}
                                    >
                                        {enabled ? (
                                            <Input
                                                disabled={disabled}
                                                name={`${row.path}.stockQuantity`}
                                                type="text"
                                                size="small"
                                                variant="filled"
                                                label={i18n.t('common.preOrderQuantity')}
                                                error={getIn(errors, `${row.path}.stockQuantity`)}
                                                showErrorBelow
                                                FormHelperTextProps={{
                                                    style: {
                                                        whiteSpace: 'normal',
                                                        overflowWrap: 'anywhere',
                                                    },
                                                }}
                                                inputProps={{ min: 0, step: 1 }}
                                                style={{ maxWidth: 140 }}
                                                onChange={(
                                                    event: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    const value = event.target.value.replace(
                                                        /\D/g,
                                                        ''
                                                    );

                                                    setFieldValue(
                                                        `${row.path}.stockQuantity`,
                                                        value
                                                    );
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="body1">-</Typography>
                                        )}
                                    </TableCell>
                                )}
                            </TableRow>
                            {!!rowErrors.length && (
                                <TableRow>
                                    <TableCell colSpan={isDedicated ? 4 : 3}>
                                        <ValidationMessages messages={rowErrors} />
                                    </TableCell>
                                </TableRow>
                            )}
                        </React.Fragment>
                    );
                })}
            </TableBody>
        </Table>
    );
}

function getValidationMessages(error: any): string[] {
    if (typeof error === 'string') return [error];
    if (!error || typeof error !== 'object') return [];
    return Object.values(error).reduce<string[]>(
        (messages, value) => messages.concat(getValidationMessages(value)),
        []
    );
}

function ValidationMessages({ messages }: { messages: string[] }) {
    return (
        <Box marginTop={0.5}>
            {Array.from(new Set(messages)).map((message) => (
                <Typography key={message} variant="body2" color="error">
                    {message}
                </Typography>
            ))}
        </Box>
    );
}

function CustomWindows({
    path,
    values,
    disabled,
    days,
    follows,
    onChange,
    todayWorkingHours,
}: any) {
    const { errors } = useFormikContext<any>();
    const windows = getIn(values, path) ?? [];

    return (
        <Box marginTop={2}>
            <FieldArray
                name={path}
                render={(arrayHelpers) => (
                    <>
                        {/* FormControlLabel is inline-flex; wrap it so later siblings (e.g. the Add window button) start on their own line. */}
                        <Box width="100%">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={follows}
                                        disabled={disabled}
                                        onChange={(_, checked) => onChange(checked)}
                                    />
                                }
                                label={
                                    <Box display="flex" flexDirection="column" gridGap={3}>
                                        <Typography variant="body1">
                                            {i18n.t('common.orderingWindowFollowsWorkingHours')}
                                        </Typography>
                                        {follows ? (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                style={{ fontSize: 13 }}
                                            >
                                                {todayWorkingHours.length
                                                    ? i18n.t(
                                                          'common.orderingWorkingHoursAutoUpdate',
                                                          {
                                                              hours: todayWorkingHours
                                                                  .map(
                                                                      (range: any) =>
                                                                          `${formatPickupTime(
                                                                              range.from
                                                                          )} - ${formatPickupTime(
                                                                              range.to
                                                                          )}`
                                                                  )
                                                                  .join(', '),
                                                          }
                                                      )
                                                    : i18n.t('common.restaurantClosedToday')}
                                            </Typography>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                style={{ fontSize: 13 }}
                                            >
                                                {i18n.t('common.orderingWindowUncheckedHint')}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                            />
                        </Box>
                        {!follows &&
                            windows.map((window: any, index: number) => (
                                <Box key={index}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gridGap={10}
                                        marginTop={1}
                                    >
                                        <FormControl
                                            variant="filled"
                                            size="small"
                                            style={{ width: 110 }}
                                        >
                                            <InputLabel>{i18n.t('common.fromDay')}</InputLabel>
                                            <Select
                                                value={window.fromDay}
                                                disabled={disabled || follows}
                                                label={i18n.t('common.fromDay')}
                                                onChange={(event) =>
                                                    arrayHelpers.form.setFieldValue(
                                                        `${path}[${index}].fromDay`,
                                                        event.target.value
                                                    )
                                                }
                                            >
                                                {days.map((day: any) => (
                                                    <MenuItem
                                                        key={day.value}
                                                        value={day.value.toLowerCase()}
                                                    >
                                                        {day.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl
                                            variant="filled"
                                            size="small"
                                            style={{ width: 110 }}
                                        >
                                            <InputLabel>{i18n.t('common.toDay')}</InputLabel>
                                            <Select
                                                value={window.toDay}
                                                disabled={disabled || follows}
                                                label={i18n.t('common.toDay')}
                                                onChange={(event) =>
                                                    arrayHelpers.form.setFieldValue(
                                                        `${path}[${index}].toDay`,
                                                        event.target.value
                                                    )
                                                }
                                            >
                                                {days.map((day: any) => (
                                                    <MenuItem
                                                        key={day.value}
                                                        value={day.value.toLowerCase()}
                                                    >
                                                        {day.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormikTimePicker
                                            name={`${path}[${index}].from`}
                                            size="small"
                                            label={i18n.t('common.fromTime')}
                                            disabled={disabled || follows}
                                            showErrorBelow
                                            style={{ marginBottom: 0 }}
                                        />
                                        <FormikTimePicker
                                            name={`${path}[${index}].to`}
                                            size="small"
                                            label={i18n.t('common.toTime')}
                                            disabled={disabled || follows}
                                            showErrorBelow
                                            style={{ marginBottom: 0 }}
                                        />
                                        <Button
                                            color="primary"
                                            disabled={disabled || windows.length <= 1}
                                            onClick={() => arrayHelpers.remove(index)}
                                        >
                                            − {i18n.t('common.removeWindow')}
                                        </Button>
                                        {index === windows.length - 1 && (
                                            <Button
                                                color="primary"
                                                disabled={disabled}
                                                onClick={() =>
                                                    arrayHelpers.push({
                                                        fromDay: 'monday',
                                                        from: null,
                                                        toDay: 'monday',
                                                        to: null,
                                                    })
                                                }
                                            >
                                                + {i18n.t('common.addWindow')}
                                            </Button>
                                        )}
                                    </Box>
                                    <ValidationMessages
                                        messages={getValidationMessages(
                                            getIn(errors, `${path}[${index}]`)
                                        )}
                                    />
                                </Box>
                            ))}
                        {!follows && !windows.length && (
                            <Button
                                color="primary"
                                disabled={disabled}
                                onClick={() =>
                                    arrayHelpers.push({
                                        fromDay: 'monday',
                                        from: null,
                                        toDay: 'monday',
                                        to: null,
                                    })
                                }
                            >
                                + {i18n.t('common.addWindow')}
                            </Button>
                        )}
                        <ValidationMessages
                            messages={
                                typeof getIn(errors, path) === 'string' ? [getIn(errors, path)] : []
                            }
                        />
                    </>
                )}
            />
        </Box>
    );
}
